import { useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';

import {
  Assets,
  Box,
  Button,
  Flex,
  useDatePickerInput,
  UseDatePickerInputArgs,
  useSelectSingle,
} from '@foradevelopment/fora-ui';
import { FAGroupViewModel, Markup, MarkupNew } from '@fora-monorepo/employer-my-pages-common/types';

import { MarkupEdit } from './MarkupEdit';
import { MarkupInfo } from './MarkupInfo';
import { MarkupForm } from '../ManageEmployment';
import { useTypedTranslation } from '../../i18n/i18n';
import { useAppSelector } from '../../redux/hooks';
import { subOneDay } from '../../employments/utils';

type MarkupChangeListProps = {
  extendedMarkupList: ExtendedMarkupType[];
  setExtendedMarkupList: (list: ExtendedMarkupType[]) => void;
  markupOptions: FAGroupViewModel[];
  markupDatePickerInputSettings: UseDatePickerInputArgs;
  faGroups: FAGroupViewModel[];
  setIsEditing: (isEditing: boolean) => void;
  // TODO: Remove when the start date of the first employment period becomes editable.
  isFirstEmploymentPeriod?: boolean;
};
export type ExtendedMarkupTypeOld = Markup & {
  newMarkup?: Markup;
  deletedMarkup?: boolean;
};

export type ExtendedMarkupType = Omit<MarkupNew, 'status'> & {
  newMarkup?: Omit<MarkupNew, 'status'>;
  deletedMarkup?: boolean;
};

export const MarkupEditList = ({
  markupOptions,
  extendedMarkupList,
  markupDatePickerInputSettings,
  setExtendedMarkupList,
  setIsEditing,
  isFirstEmploymentPeriod,
}: MarkupChangeListProps) => {
  const { ffFaGroupIdOnEmployment } = useAppSelector(state => state.foraGlobal.settings);

  const [editIndexes, setEditIndexes] = useState<number[]>([]);
  const [showAddNewMarkup, setShowAddNewMarkup] = useState<boolean>(false);

  const markupSelectInput = useSelectSingle();
  const { t } = useTypedTranslation();
  const markupStartDateInput = useDatePickerInput(markupDatePickerInputSettings);

  const sortedExtendedMarkupList = useMemo<
    Array<ExtendedMarkupType & { originalIndex: number; ends: string | null }>
  >(() => {
    const sortedList = extendedMarkupList
      .map((markup, originalIndex) => ({ markup, originalIndex }))
      .sort((a, b) => {
        const dateA = a.markup.newMarkup?.startDate ?? a.markup.startDate;
        const dateB = b.markup.newMarkup?.startDate ?? b.markup.startDate;
        return dateA > dateB ? -1 : dateA < dateB ? 1 : 0;
      });

    return sortedList.map(({ markup, originalIndex }, i) => ({
      ...markup,
      originalIndex,
      ends: sortedList[i - 1]
        ? subOneDay(sortedList[i - 1].markup.newMarkup?.startDate ?? sortedList[i - 1].markup.startDate)
        : null,
    }));
  }, [extendedMarkupList]);

  useEffect(() => {
    setIsEditing(showAddNewMarkup || editIndexes.length > 0);
  }, [editIndexes, setIsEditing, showAddNewMarkup]);

  const handleAdd = () => {
    const markupStartDateValidationErrors = markupStartDateInput.validateDateInputValue();

    const matchingMarkupOption = markupOptions.find(
      o => (ffFaGroupIdOnEmployment ? o.faGroupId : o.fokId) === markupSelectInput.props.value
    );

    if (markupSelectInput.validate() && !markupStartDateValidationErrors.length && matchingMarkupOption) {
      const newMarkup: ExtendedMarkupType = {
        faGroupId: matchingMarkupOption.faGroupId,
        fokText: matchingMarkupOption.fokText,
        fokId: matchingMarkupOption.fokId,
        startDate: markupStartDateInput.value,
        endDate: null,
      };

      setExtendedMarkupList([...extendedMarkupList, newMarkup]);
      setShowAddNewMarkup(false);

      markupSelectInput.props.setValue('');
      markupStartDateInput.props.setCalendarDate(null);
    }
  };

  const handleCancelAdd = () => {
    markupSelectInput.props.setValue('');
    markupStartDateInput.props.setCalendarDate(null);
    markupStartDateInput.props.errors = [];
    setShowAddNewMarkup(false);
  };

  const handleCancelEdit = (index: number) => {
    setEditIndexes(prev => prev.filter(i => i !== index));
  };

  const handleDelete = (index: number) => {
    const updatedList = [...extendedMarkupList];
    updatedList[index] = {
      ...updatedList[index],
      deletedMarkup: true,
    };
    setExtendedMarkupList(updatedList);
  };

  const handleRevert = (index: number) => {
    if (extendedMarkupList[index].deletedMarkup === true) {
      const updatedList = [...extendedMarkupList];
      updatedList[index] = {
        ...updatedList[index],
        deletedMarkup: false,
      };
      setExtendedMarkupList(updatedList);
    } else if (extendedMarkupList[index].newMarkup) {
      const updatedList = [...extendedMarkupList];
      delete updatedList[index].newMarkup;
      setExtendedMarkupList(updatedList);
    }
  };

  const handleSubmit = (index: number, newMarkup: MarkupNew) => {
    const updatedList = [...extendedMarkupList];
    const existingMarkup = updatedList[index];
    if (
      existingMarkup &&
      existingMarkup.startDate === newMarkup.startDate &&
      existingMarkup.fokId === newMarkup.fokId
    ) {
      setEditIndexes(prev => prev.filter(i => i !== index));
      return;
    }

    if (extendedMarkupList[index].deletedMarkup === true) {
      updatedList[index] = {
        ...updatedList[index],
        deletedMarkup: false,
        newMarkup,
      };
    } else {
      updatedList[index] = {
        ...updatedList[index],
        newMarkup,
      };
    }
    setExtendedMarkupList(updatedList);
    setEditIndexes(prev => prev.filter(i => i !== index));
  };

  return (
    <Flex rowGap={30} flexDirection="column">
      <Box>
        {!showAddNewMarkup ? (
          <Button
            variant="outline"
            onClick={() => setShowAddNewMarkup(true)}
            fullWidth={false}
            testId="add-new-markup-button"
          >
            <Assets.PlusIcon marginRight={6} />
            {t('form.editEmployment.newMarkup')}
          </Button>
        ) : (
          <MarkupForm
            ffFaGroupIdOnEmployment={ffFaGroupIdOnEmployment}
            markupOptions={markupOptions}
            startDateInputProps={markupStartDateInput.props}
            startDateInputValue={markupStartDateInput.value}
            markupSelectInput={markupSelectInput}
            formType="add"
            onSubmit={handleAdd}
            onCancel={handleCancelAdd}
          />
        )}
      </Box>
      <AnimatePresence mode="popLayout">
        {sortedExtendedMarkupList.map((eachMarkup, i) => (
          <motion.div
            key={`container-${eachMarkup.originalIndex}`}
            layout="position"
            initial={{ opacity: 0, y: -50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2 }}
          >
            {editIndexes.includes(eachMarkup.originalIndex) ? (
              <MarkupEdit
                ffFaGroupIdOnEmployment={ffFaGroupIdOnEmployment}
                key={eachMarkup.originalIndex}
                onSubmit={({ fokId, startDate, fokText, faGroupId }) =>
                  handleSubmit(eachMarkup.originalIndex, { startDate, fokId, fokText, faGroupId } as MarkupNew)
                }
                markupOptions={markupOptions}
                selectedMarkup={eachMarkup.newMarkup ? eachMarkup.newMarkup : eachMarkup}
                onCancel={() => handleCancelEdit(eachMarkup.originalIndex)}
                markupDatePickerInputSettings={markupDatePickerInputSettings}
                editable={!(isFirstEmploymentPeriod && eachMarkup.originalIndex === 0)}
              />
            ) : (
              <MarkupInfo
                key={eachMarkup.originalIndex}
                markup={eachMarkup}
                markupOptions={markupOptions}
                onClick={() => setEditIndexes(prev => [...prev, eachMarkup.originalIndex])}
                onDeleteClick={() => handleDelete(eachMarkup.originalIndex)}
                onRevertClick={() => handleRevert(eachMarkup.originalIndex)}
                editable={!(isFirstEmploymentPeriod && eachMarkup.originalIndex === 0)}
                ends={eachMarkup.ends}
              />
            )}
          </motion.div>
        ))}
      </AnimatePresence>
    </Flex>
  );
};
